import type {
  Diagram,
  LogicNode,
  MainItemType,
  State,
} from '../../../types/EMRALD_Model';
import type { ModelItem } from '../../../types/ModelUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActionContext } from '../../../contexts/ActionContext';
import { useAlertContext } from '../../../contexts/AlertContext';
import { useDiagramContext } from '../../../contexts/DiagramContext';
import { useEventContext } from '../../../contexts/EventContext';
import { useExtSimContext } from '../../../contexts/ExtSimContext';
import { useLogicNodeContext } from '../../../contexts/LogicNodeContext';
import { useStateContext } from '../../../contexts/StateContext';
import { useVariableContext } from '../../../contexts/VariableContext';
import { useWindowContext } from '../../../contexts/WindowContext';
import { GetModelItemsReferencedBy } from '../../../utils/ModelReferences';
import { currentDiagram } from '../../diagrams/EmraldDiagram/EmraldDiagram';
import { useLogicNodeTreeDiagram } from '../../diagrams/LogicTreeDiagram/useLogicTreeDiagram';

export function useSidebarLogic() {
  const { diagrams, getDiagramByDiagramName } = useDiagramContext();
  const { logicNodes } = useLogicNodeContext();
  const { actions } = useActionContext();
  const { events } = useEventContext();
  const { states } = useStateContext();
  const { variables } = useVariableContext();
  const { extSims } = useExtSimContext();

  const [isDiagramAccordionOpen, setIsDiagramAccordionOpen] = useState(false);
  const [isComponentAccordionOpen, setIsComponentAccordionOpen]
    = useState(false);
  const [componentGroup, setComponentGroup] = useState('all');
  const [currDiagram, setCurrDiagram] = useState(currentDiagram.value);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ModelItem | undefined>();
  const [itemToDeleteType, setItemToDeleteType] = useState<MainItemType>();
  const { deleteState } = useStateContext();
  const { deleteDiagram, updateDiagram } = useDiagramContext();
  const { deleteLogicNode } = useLogicNodeContext();
  const { deleteExtSim } = useExtSimContext();
  const { deleteAction } = useActionContext();
  const { deleteEvent } = useEventContext();
  const { deleteVariable } = useVariableContext();
  const { closeAllWindows } = useWindowContext();
  const { recurseAndDeleteChildren } = useLogicNodeTreeDiagram();
  const { activeWindowId, getWindowTitleById } = useWindowContext();
  const { showAlert } = useAlertContext();

  const onDiagramChange = (newDiagram: Diagram) => {
    // Update currDiagram state
    setCurrDiagram(newDiagram);
  };

  // Set the current diagram to the one corresponding to the active window
  useEffect(() => {
    // Get the title of the active window
    const activeWindowTitle = getWindowTitleById(activeWindowId) ?? '';

    // Set the current diagram to the one corresponding to the active window title
    const d = getDiagramByDiagramName(activeWindowTitle);
    if (d) {
      setCurrDiagram(d);
    }
  }, [activeWindowId]);

  const diagramPanels = [
    { type: 'Diagrams', data: diagrams },
    { type: 'Logic Tree', data: logicNodes },
    { type: 'External Sims', data: extSims },
  ];

  const stateItems = useMemo(() => {
    if (componentGroup === 'all') {
      return states;
    } else if (componentGroup === 'local') {
      try {
        const copyModel = GetModelItemsReferencedBy(
          currDiagram.name,
          'Diagram',
          1,
        );
        return copyModel.StateList;
      } catch (error) {
        console.error('Error Message:', error);
        showAlert('Unable to get referenced StateList items', 'error');
      }
    } else {
      return states; // TODO: Add condition for what to show when states are local
    }
  }, [componentGroup, states, currDiagram]);

  const actionItems = useMemo(() => {
    switch (componentGroup) {
      case 'all': {
        return actions;
      }
      case 'global': {
        return actions.filter(item => item.mainItem);
      }
      case 'local': {
        try {
          const copyModel = GetModelItemsReferencedBy(
            currDiagram.name,
            'Diagram',
            2,
          );
          return copyModel.ActionList;
        } catch (error) {
          console.error('Error Message:', error);
          showAlert('Unable to get referenced ActionList items', 'error');
        }

        break;
      }
      default: {
        return [];
      }
    }
  }, [componentGroup, actions, currDiagram]);

  const eventItems = useMemo(() => {
    switch (componentGroup) {
      case 'all': {
        return events;
      }
      case 'global': {
        return events.filter(item => item.mainItem);
      }
      case 'local': {
        try {
          const copyModel = GetModelItemsReferencedBy(
            currDiagram.name,
            'Diagram',
            2,
          );
          return copyModel.EventList;
        } catch (error) {
          console.error('Error Message:', error);
          showAlert('Unable to get referenced EventList items', 'error');
        }

        break;
      }
      default: {
        return [];
      }
    }
  }, [componentGroup, events, currDiagram]);

  const componentPanels = useMemo(() => {
    if (componentGroup === 'global') {
      return [
        { type: 'Actions', data: actionItems },
        { type: 'Events', data: eventItems },
        { type: 'Variables', data: variables },
      ];
    } else if (componentGroup === 'local') {
      return [
        { type: 'Actions', data: actionItems },
        { type: 'Events', data: eventItems },
        { type: 'States', data: stateItems },
      ];
    }
    return [
      { type: 'Actions', data: actionItems },
      { type: 'Events', data: eventItems },
      { type: 'States', data: stateItems },
      { type: 'Variables', data: variables },
    ];
  }, [componentGroup, actionItems, eventItems, variables, stateItems]);

  const bothAccordionsOpen = useMemo(() => {
    return isDiagramAccordionOpen && isComponentAccordionOpen;
  }, [isDiagramAccordionOpen, isComponentAccordionOpen]);

  const defaultDrawerWidth = 245;
  const minDrawerWidth = 215;
  const maxDrawerWidth = 1000;

  const [drawerWidth, setDrawerWidth] = useState(defaultDrawerWidth);

  const handleMouseDown = () => {
    document.addEventListener('mouseup', handleMouseUp as EventListener, true);
    document.addEventListener(
      'mousemove',
      handleMouseMove as EventListener,
      true,
    );
  };

  const handleMouseUp = () => {
    document.removeEventListener(
      'mouseup',
      handleMouseUp as EventListener,
      true,
    );
    document.removeEventListener(
      'mousemove',
      handleMouseMove as EventListener,
      true,
    );
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const newWidth = e.clientX - document.body.offsetLeft;
      if (newWidth > minDrawerWidth && newWidth < maxDrawerWidth) {
        setDrawerWidth(newWidth);
      }
    },
    [minDrawerWidth, maxDrawerWidth],
  );

  const closeDeleteConfirmation = () => {
    // * Closes the confirmation dialog and resets the state items
    setDeleteConfirmation(false);
    setItemToDelete(undefined);
  };

  const deleteItem = () => {
    if (!itemToDelete) {
      return;
    }
    if (itemToDeleteType === 'Diagram') {
      deleteDiagram(itemToDelete.id);
      closeDeleteConfirmation();
    }
    if (itemToDeleteType === 'LogicNode') {
      const nodeToDelete = itemToDelete as unknown as LogicNode;
      // Pass nodeToDelete as the explicit deletion root so canDeleteNode lets the recursion
      // enter an isRoot tree-top. Without this, the closure `rootNode` is null in the sidebar
      // context and the entry-point gate would block any descendant cleanup, leaving orphans.
      recurseAndDeleteChildren(nodeToDelete, nodeToDelete);
      deleteLogicNode(nodeToDelete.id);
    }
    if (itemToDeleteType === 'ExtSim') {
      deleteExtSim(itemToDelete.id);
    }
    if (itemToDeleteType === 'Action') {
      deleteAction(itemToDelete.id);
    }
    if (itemToDeleteType === 'Event') {
      deleteEvent(itemToDelete.id);
    }
    if (itemToDeleteType === 'State') {
      // not sure why the next 4 lines are needed, but if not then the diagram containin ght state has a ghost state left in it when you open it
      const diagram = getDiagramByDiagramName(
        (itemToDelete as State).diagramName,
      );
      if (diagram) {
        const updatedStates = diagram.states.filter(
          stateName => stateName !== itemToDelete.name,
        );
        diagram.states = updatedStates;
        updateDiagram(diagram);
      }
      deleteState(itemToDelete.id);
    }
    if (itemToDeleteType === 'Variable') {
      deleteVariable(itemToDelete.id);
    }
    setDeleteConfirmation(false);
    closeAllWindows();
  };

  const handleDelete = (itemToDelete: ModelItem, itemType: MainItemType) => {
    setDeleteConfirmation(true);
    setItemToDelete(itemToDelete);
    setItemToDeleteType(itemType);
  };

  return {
    componentGroup,
    bothAccordionsOpen,
    drawerWidth,
    diagramPanels,
    componentPanels,
    deleteConfirmation,
    itemToDelete,
    setDeleteConfirmation,
    setItemToDelete,
    closeDeleteConfirmation,
    deleteItem,
    handleDelete,
    setIsDiagramAccordionOpen,
    setIsComponentAccordionOpen,
    setComponentGroup,
    handleMouseDown,
    onDiagramChange,
  };
}
